import { useQuery } from '@tanstack/react-query';
import {
  ActiveSubstanceService,
  activeSubstanceKeys,
} from '@client/services/activeSubstanceService';

/**
 * Hook to fetch a single active substance by identifier
 * @param {string|number} identifier - UUID or name of the active substance
 * @param {Object} [options={}] - Additional React Query options
 * @param {boolean} [options.enabled] - Whether the query should run
 * @param {number} [options.staleTime] - Time in ms before data becomes stale
 * @param {Function} [options.onSuccess] - Callback when query succeeds
 * @param {Function} [options.onError] - Callback when query fails
 * @returns {Object} React Query result object with data, loading, error states
 * @example
 * const { data: substance, isLoading, error } = useActiveSubstance('123');
 */
export const useActiveSubstance = (identifier, options = {}) => {
  return useQuery({
    queryKey: activeSubstanceKeys.detail(identifier),
    queryFn: () => ActiveSubstanceService.getActiveSubstance(identifier),
    enabled: !!identifier,
    ...options,
  });
};

/**
 * Hook to fetch a list of active substances with optional filtering
 * @param {Object} [filters={}] - Query parameters for filtering the list
 * @param {string} [filters.search] - Search term for substance names
 * @param {string} [filters.category] - Filter by category
 * @param {number} [filters.page] - Page number for pagination
 * @param {number} [filters.limit] - Number of items per page
 * @param {Object} [options={}] - Additional React Query options
 * @param {boolean} [options.enabled] - Whether the query should run
 * @param {number} [options.staleTime] - Time in ms before data becomes stale
 * @param {Function} [options.onSuccess] - Callback when query succeeds
 * @param {Function} [options.onError] - Callback when query fails
 * @returns {Object} React Query result object with data array, loading, error states
 * @example
 * const { data: substances, isLoading } = useActiveSubstances({ search: 'aspirin' });
 */
export const useActiveSubstances = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: activeSubstanceKeys.filter(filters),
    queryFn: () => ActiveSubstanceService.filterActiveSubstances(filters),
    ...options,
  });
};

/**
 * Fetches culture tree data for an active substance
 * @param {string} activeSubstanceId - The active substance identifier
 * @param {Object} params - Query parameters for the API
 * @param {Object} options - React Query options
 * @returns {Object} Query result with tree data
 */
export const useActiveSubstanceCultureTree = (
  activeSubstanceId,
  params = {},
  options = {}
) => {
  return useQuery({
    queryKey: activeSubstanceKeys.cultureTree(activeSubstanceId, params),
    queryFn: () =>
      ActiveSubstanceService.getCultureTree(activeSubstanceId, params),
    enabled: !!activeSubstanceId && options.enabled !== false,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};
